import React, { useState } from 'react';
import { useRange } from 'react-instantsearch';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface RangeSliderProps {
  attribute: string;
  title: string;
  expanded?: boolean;
  min?: number;
  max?: number;
  step?: number;
  precision?: number;
}

export const RangeSlider: React.FC<RangeSliderProps> = ({
  attribute,
  title,
  expanded = true,
  min = 0,
  max = 100,
  step = 1,
  precision = 0,
}) => {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const { start, range, canRefine, refine } = useRange({
    attribute,
    min,
    max,
    precision,
  });

  if (!canRefine) {
    return null;
  }

  const formatValue = (value: number) => {
    if (attribute === 'price') {
      return value === max ? `$${value}+` : `$${value}`;
    }
    return value.toString();
  };

  const currentMin = start[0] ?? min;
  const currentMax = start[1] ?? max;

  const handleValueChange = (values: number[]) => {
    const [newMin, newMax] = values;
    refine([
      newMin === min ? undefined : newMin,
      newMax === max ? undefined : newMax,
    ]);
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between p-2 h-auto">
          <span className="font-medium">{title}</span>
          <ChevronDown 
            className={cn(
              "h-4 w-4 transition-transform", 
              isExpanded && "rotate-180"
            )} 
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="p-2 space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>{formatValue(min)}</span>
            <span>{formatValue(max)}</span>
          </div>
          <Slider
            value={[currentMin, currentMax]}
            onValueChange={handleValueChange}
            min={min}
            max={max}
            step={step}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{formatValue(currentMin)}</span>
            <span>{formatValue(currentMax)}</span>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};